/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import { SearchIcon, XIcon } from 'lucide-react'
import type { ComponentProps, KeyboardEvent } from 'react'
import { forwardRef } from 'react'

import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/ui/input-group'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'

export interface SearchInputProps {
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
  'aria-label'?: string
  autoFocus?: boolean
  /** English default for clear control; override when wiring i18n at the app layer. */
  clearLabel?: string
  loading?: boolean
  disabled?: boolean
  className?: string
  inputProps?: ComponentProps<typeof InputGroupInput>
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>((props, forwardedRef) => {
  const {
    value,
    onValueChange,
    placeholder,
    autoFocus,
    clearLabel = 'Clear search',
    loading = false,
    disabled = false,
    className,
    inputProps,
    'aria-label': ariaLabel,
  } = props

  const hasValue = value.length > 0

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    inputProps?.onKeyDown?.(event)
    if (event.defaultPrevented) {
      return
    }
    if (event.key === 'Escape' && hasValue) {
      event.preventDefault()
      onValueChange('')
    }
  }

  return (
    <InputGroup className={cn('h-10', className)}>
      <InputGroupAddon align="inline-start">
        <SearchIcon aria-hidden className="size-4 text-muted-foreground" />
      </InputGroupAddon>
      <InputGroupInput
        {...inputProps}
        aria-label={ariaLabel ?? placeholder}
        autoFocus={autoFocus}
        className={cn('[&::-webkit-search-cancel-button]:hidden', inputProps?.className)}
        disabled={disabled || inputProps?.disabled}
        onChange={(event) => {
          inputProps?.onChange?.(event)
          onValueChange(event.target.value)
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        ref={forwardedRef}
        type={inputProps?.type ?? 'search'}
        value={value}
      />
      {loading || hasValue ? (
        <InputGroupAddon align="inline-end">
          {loading ? <Spinner className="size-4 text-muted-foreground" /> : null}
          {hasValue ? (
            <InputGroupButton
              aria-label={clearLabel}
              disabled={disabled}
              onClick={() => onValueChange('')}
              size="icon-xs"
              type="button"
            >
              <XIcon aria-hidden />
            </InputGroupButton>
          ) : null}
        </InputGroupAddon>
      ) : null}
    </InputGroup>
  )
})

SearchInput.displayName = 'SearchInput'
